/**
 * V4GovernanceDiffModal — Phase 7i.
 *
 * Fires when a save is rejected by the WorkflowPhaseAllowlistGuard
 * (BE returns 409 / 423 with a structured diff body). The modal shows
 * what the BE refused (added / removed / mutated nodes) and offers a
 * "Regress" action that demotes the linked agent's lifecycle phase
 * (Act → Decide → Build) so the same save can succeed on retry.
 *
 * Pure presentational — the host owns:
 *   • opening the modal in response to res.ok === false && (status 409|423)
 *   • the Regress API call (POST /agents/{id}/regress-phase)
 *   • the re-save after a successful regress
 *
 * V3 parity:
 *   • Same 3 diff sections (attemptedAdds / attemptedRemoves /
 *     attemptedMutates) — backend-computed, FE just renders strings.
 *   • Same Regress-target driver — the BE includes `regressTarget`
 *     ("Decide" | "Build") in the rejection body; we surface it in
 *     the button label so the operator sees "Regress to Decide"
 *     before they click.
 *   • Same cancel semantics — the operator's pending edits remain
 *     unsaved on the canvas (the BE never wrote them), so Cancel is
 *     non-destructive; the only thing that happens is the modal closes.
 *
 * Tagged PHASE-7I.
 */

import React, { useEffect } from 'react';
import { ShieldAlert, X, Plus, Minus, Edit3, ArrowDownCircle } from 'lucide-react';
import {
  S, BRAND, ACCENT, SPACING, RADIUS, FONT, WEIGHT, SHADOW, Z,
  primaryBtn, secondaryBtn, ghostBtn,
} from './v3Tokens';

const MONO = '"JetBrains Mono", "Fira Code", ui-monospace, monospace';

export default function V4GovernanceDiffModal({
  open,
  // Diff payload from the BE rejection. Shape (per V3 audit, all keys optional):
  //   { error, message, attemptedAdds, attemptedRemoves, attemptedMutates,
  //     regressTarget, phase, status (HTTP code) }
  diff,
  // Linked agent + current phase. Drives the Regress button label.
  // Pass null if the agent hasn't loaded yet — Regress will be disabled.
  agent,
  // In-flight flags drive the button disable + spinner.
  regressing = false,
  resaving = false,
  // Handlers.
  onRegress,   // (targetPhase) => Promise<void>
  onCancel,    // () => void
}) {
  // Esc closes — unless an action is in flight (don't blow away the
  // operator's window mid-PUT).
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !regressing && !resaving) {
        e.preventDefault();
        onCancel?.();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, regressing, resaving, onCancel]);

  if (!open) return null;

  const adds    = Array.isArray(diff?.attemptedAdds)    ? diff.attemptedAdds    : [];
  const removes = Array.isArray(diff?.attemptedRemoves) ? diff.attemptedRemoves : [];
  const mutates = Array.isArray(diff?.attemptedMutates) ? diff.attemptedMutates : [];
  const totalChanges = adds.length + removes.length + mutates.length;

  const target = diff?.regressTarget || null;
  const currentPhase = agent?.phase || diff?.phase || null;
  const busy = regressing || resaving;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Save blocked by protection"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: Z.modal,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '10vh',
      }}
    >
      <div style={{
        width: 'min(560px, 92vw)',
        background: '#ffffff',
        borderRadius: RADIUS.xl,
        boxShadow: SHADOW.popover,
        overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        maxHeight: '78vh',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: SPACING.s,
          padding: `${SPACING.m}px ${SPACING.l}px`,
          borderBottom: `0.5px solid ${S[200]}`,
        }}>
          <ShieldAlert size={16} aria-hidden="true" style={{ color: ACCENT.amber.icon, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: FONT.lg, fontWeight: WEIGHT.semibold, color: S[900] }}>
              Save blocked by protection
            </div>
            <div style={{ fontSize: FONT.xs, color: S[500], marginTop: 2 }}>
              {currentPhase
                ? `Agent is in ${String(currentPhase).toUpperCase()} phase — the platform refused the structural change. `
                : 'The platform refused the structural change. '}
              Regress the agent’s phase to make these edits.
            </div>
          </div>
          <button
            type="button"
            onClick={() => { if (!busy) onCancel?.(); }}
            aria-label="Close"
            style={ghostBtn}
            disabled={busy}
          >
            <X size={14} />
          </button>
        </div>

        {/* Body — diff sections */}
        <div style={{
          flex: 1, minHeight: 0,
          padding: SPACING.l,
          overflowY: 'auto',
          display: 'flex', flexDirection: 'column', gap: SPACING.m,
        }}>
          {diff?.error && (
            <div style={{
              padding: SPACING.s + 2,
              fontSize: FONT.sm, color: ACCENT.amber.text,
              background: ACCENT.amber.bg,
              border: `0.5px solid ${ACCENT.amber.icon}`,
              borderRadius: RADIUS.md,
              fontFamily: 'inherit',
            }}>
              {diff.error}
            </div>
          )}

          {totalChanges === 0 && !diff?.error && (
            <div style={{
              padding: SPACING.l, textAlign: 'center',
              fontSize: FONT.sm, color: S[500],
            }}>
              No structural diff was reported. The platform may have
              refused for a non-diffable reason (status badge,
              unsupported transition).
            </div>
          )}

          <Section
            icon={<Plus size={12} aria-hidden="true" />}
            color={ACCENT.emerald}
            label="Added"
            items={adds}
          />
          <Section
            icon={<Minus size={12} aria-hidden="true" />}
            color={ACCENT.rose}
            label="Removed"
            items={removes}
          />
          <Section
            icon={<Edit3 size={12} aria-hidden="true" />}
            color={ACCENT.sky}
            label="Mutated"
            items={mutates}
          />
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: SPACING.s,
          padding: `${SPACING.s + 2}px ${SPACING.l}px`,
          borderTop: `0.5px solid ${S[200]}`,
          background: S[50],
        }}>
          <span style={{ fontSize: FONT.xs, color: S[500] }}>
            {regressing
              ? 'Regressing agent phase…'
              : resaving
                ? 'Re-saving with the new phase…'
                : (target
                  ? `Regress will move the agent to ${String(target).toUpperCase()} and re-fire this save.`
                  : 'No regress target reported — fix the workflow or open in V3.')}
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: SPACING.s }}>
            <button
              type="button"
              onClick={() => { if (!busy) onCancel?.(); }}
              style={secondaryBtn}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => { if (target && !busy && typeof onRegress === 'function') onRegress(target); }}
              disabled={!target || !agent || busy}
              style={{
                ...primaryBtn,
                background: ACCENT.amber.icon,
                opacity: (!target || !agent || busy) ? 0.5 : 1,
                cursor: (!target || !agent || busy) ? 'not-allowed' : 'pointer',
              }}
            >
              <ArrowDownCircle size={11} aria-hidden="true" />
              {target ? `Regress to ${String(target)}` : 'Regress'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───── Section — one row per diff bucket ───────────────────────── */
function Section({ icon, color, label, items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={{
      border: `0.5px solid ${S[200]}`,
      borderRadius: RADIUS.md,
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: SPACING.xs,
        padding: `${SPACING.xs + 2}px ${SPACING.s + 2}px`,
        background: color.bg,
        color: color.text,
        fontSize: FONT.xs, fontWeight: WEIGHT.bold,
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}>
        {icon}
        <span>{label}</span>
        <span style={{ marginLeft: 'auto', fontWeight: WEIGHT.medium }}>
          {items.length}
        </span>
      </div>
      <ul style={{
        listStyle: 'none', margin: 0, padding: 0,
        background: '#ffffff',
      }}>
        {items.map((item, i) => (
          <li
            key={`${label}_${i}_${typeof item === 'string' ? item : i}`}
            style={{
              padding: `${SPACING.xs + 2}px ${SPACING.s + 2}px`,
              fontSize: FONT.sm, color: S[800],
              fontFamily: MONO,
              borderTop: i === 0 ? 'none' : `0.5px solid ${S[100]}`,
              wordBreak: 'break-word',
            }}
          >
            {typeof item === 'string' ? item : JSON.stringify(item)}
          </li>
        ))}
      </ul>
    </div>
  );
}
